import type { DetailsHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from "react";

type ButtonProps = {
  children: ReactNode;
  href?: string;
  variant?: "primary" | "secondary" | "ghost";
  className?: string;
  type?: "button" | "submit" | "reset";
  onClick?: () => void;
};

const buttonVariants = {
  primary: "bg-parcel-orange text-white hover:bg-parcel-orange/90 shadow-lg shadow-parcel-orange/20",
  secondary: "bg-secondary text-white hover:bg-secondary/90 shadow-lg shadow-secondary/20",
  ghost: "bg-white text-primary border border-border-pale hover:bg-surface-container-low"
};

export function Button({ children, href, variant = "primary", className = "", type = "button", onClick }: ButtonProps) {
  const classes = `typo-button inline-flex items-center justify-center gap-2 rounded-card px-6 py-3 transition-all active:scale-95 ${buttonVariants[variant]} ${className}`;

  if (href) {
    return (
      <a className={classes} href={href}>
        {children}
      </a>
    );
  }

  return (
    <button className={classes} type={type} onClick={onClick}>
      {children}
    </button>
  );
}

export function Card({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <article className={`bg-white rounded-card border border-border-pale shadow-sm ${className}`}>{children}</article>;
}

export function FieldInput({ className = "", ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      className={`typo-input w-full rounded-xl border border-border-pale bg-surface-container-lowest px-4 py-3 outline-none transition-all focus:border-ocean-cyan focus:ring-2 focus:ring-ocean-cyan/20 ${className}`}
      {...props}
    />
  );
}

export function FieldTextarea({ className = "", ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      className={`typo-input w-full rounded-xl border border-border-pale bg-surface-container-lowest px-4 py-3 outline-none transition-all focus:border-ocean-cyan focus:ring-2 focus:ring-ocean-cyan/20 ${className}`}
      {...props}
    />
  );
}

export function FieldSelect({ className = "", children, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      className={`typo-input w-full rounded-xl border border-border-pale bg-surface-container-lowest px-4 py-3 outline-none transition-all focus:border-ocean-cyan focus:ring-2 focus:ring-ocean-cyan/20 ${className}`}
      {...props}
    >
      {children}
    </select>
  );
}

export function StatusBadge({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <span className={`typo-status-tag inline-flex items-center rounded-full ${className}`}>{children}</span>;
}

export function FAQItem({
  question,
  answer,
  className = "",
  summaryClassName = "",
  answerClassName = "",
  icon = "add",
  ...props
}: {
  question: string;
  answer: string;
  className?: string;
  summaryClassName?: string;
  answerClassName?: string;
  icon?: "add" | "expand_more";
} & DetailsHTMLAttributes<HTMLDetailsElement>) {
  return (
    <details className={`group rounded-xl border border-border-pale bg-white p-5 shadow-sm ${className}`} {...props}>
      <summary className={`typo-card-title flex cursor-pointer list-none items-center justify-between gap-4 text-primary ${summaryClassName}`}>
        {question}
        {icon === "expand_more" ? (
          <span className="material-symbols-outlined text-secondary transition-transform group-open:rotate-180">expand_more</span>
        ) : (
          <span className="text-secondary transition-transform group-open:rotate-45">+</span>
        )}
      </summary>
      <p className={`typo-card-body mt-4 text-on-surface-variant ${answerClassName}`}>{answer}</p>
    </details>
  );
}
